import { Command } from "@/commands/Command.js"
import { calculateRelationshipLayout } from "@/modules/canvas/relationship-layout.js"
import {
    cloneConnectionRoute,
    setConnectionRoute,
} from "@/modules/canvas/connection-routing.js"
import { Note } from "@/modules/canvas/table/Note.js"
import { Table } from "@/modules/canvas/table/Table.js"

export class SortItemsCommand extends Command {
    _description = "Sort Items"
    oldPositions = []
    capturedPositions = false
    oldConnectionRoutes = []
    routeTables = []
    capturedConnectionRoutes = false

    fitItemsToScreen(renderedItems, positions = null) {
        if (!renderedItems.length) return

        const positionFor = item => positions?.get(item.data) || item.data
        this.tab.canvas.minX = Math.min(...renderedItems.map(item => positionFor(item).left))
        this.tab.canvas.minY = Math.min(...renderedItems.map(item => positionFor(item).top))
        this.tab.canvas.maxX = Math.max(...renderedItems.map(item => positionFor(item).left + item.width))
        this.tab.canvas.maxY = Math.max(...renderedItems.map(item => positionFor(item).top + item.height))
        this.tab.canvas.fitToScreen()
    }

    resetConnectionWaypoints(tables) {
        for (const table of tables) {
            for (const field of table.fields || []) {
                const route = cloneConnectionRoute(field.connection_route)
                if (!route?.waypoints.length) continue

                if (!this.capturedConnectionRoutes) {
                    this.oldConnectionRoutes.push({
                        field,
                        route,
                    })
                }
                if (!this.routeTables.includes(table)) {
                    this.routeTables.push(table)
                }

                setConnectionRoute(field, {
                    ...route,
                    waypoints: [],
                })
            }
        }
        this.capturedConnectionRoutes = true
    }

    restoreConnectionRoutes() {
        for (const { field, route } of this.oldConnectionRoutes) {
            setConnectionRoute(field, route)
        }
    }

    async saveRouteChanges(schema) {
        for (const table of this.routeTables) {
            await schema.saveTable({
                data: table,
                saveSilently: true,
            })
        }
    }

    async persistChanges() {
        const schema = ((await import('../stores/schema.store')).useSchemaStore)()
        await this.saveRouteChanges(schema)
        await schema.saveSortedItems()
    }

    async run() {
        // update canvas height first
        const canvasParentEl = document.querySelector('.schema-editor')
        if (canvasParentEl) {
            const rect = canvasParentEl.getBoundingClientRect()
            this.tab.canvas.updateCanvasSize(rect.width, rect.height)
        }


        const tables = this.tab.schema.schema_data.tables
        const notes = this.tab.schema.schema_data.notes
        const items = tables.concat(notes)
        if (!this.capturedPositions) {
            this.oldPositions = items.map(item => [item.left, item.top])
            this.capturedPositions = true
        }
        this.resetConnectionWaypoints(tables)

        const tableItems = tables.map(table => new Table(this.tab.canvas, table))
        const noteItems = notes.map(note => new Note(this.tab.canvas, note))
        const positions = calculateRelationshipLayout(tableItems, noteItems, {
            maxWidth: this.tab.canvas.width - 80,
        })

        for (const item of items) {
            const position = positions.get(item)
            if (!position) continue
            item.left = position.left
            item.top = position.top
        }

        const renderedItems = tableItems.concat(noteItems)
        this.fitItemsToScreen(renderedItems, positions)

        super.run()
        return positions
    }

    async redo() {
        await this.run()
        await this.persistChanges()
    }

    async undo() {
        let items = this.tab.schema.schema_data.tables.concat(this.tab.schema.schema_data.notes)
        for (let i = 0; i < items.length; i++) {
            let item = items[i]
            item.left = this.oldPositions[i][0]
            item.top = this.oldPositions[i][1]
        }
        this.restoreConnectionRoutes()

        const tables = this.tab.schema.schema_data.tables
            .map(table => new Table(this.tab.canvas, table))
        const notes = this.tab.schema.schema_data.notes
            .map(note => new Note(this.tab.canvas, note))
        this.fitItemsToScreen(tables.concat(notes))

        super.undo()
        await this.persistChanges()
    }
}
